import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { InvalidTokenPage } from './InvalidTokenPage.tsx';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://ymytutoring.com/api/client';

interface GradeData {
  type: 'task' | 'entry';
  data: {
    public_task_id?: string;
    entry_id?: number;
    course: string;
    entry_date?: string;
    token: string;
  };
}

export const GradeSubmissionPage: React.FC = () => {
  const { token, type } = useParams<{ token: string; type: string }>();
  const [loading, setLoading] = useState(true);
  const [gradeData, setGradeData] = useState<GradeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gradeValue, setGradeValue] = useState('');
  const [totalPoints, setTotalPoints] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchGradeData = async () => {
      try {
        const response = await fetch(`${API_BASE}/public/grade/${token}/${type}`);
        const data = await response.json();

        if (data.success) {
          setGradeData(data);
        } else {
          setError(data.message || 'Invalid or expired token');
        }
      } catch (err) {
        setError('Failed to load grade data');
      } finally {
        setLoading(false);
      }
    };

    if (token && type) {
      fetchGradeData();
    }
  }, [token, type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const grade = parseFloat(gradeValue);
    const total = parseFloat(totalPoints);

    if (isNaN(grade) || isNaN(total) || total <= 0) {
      alert('Please enter valid grade values');
      return;
    }

    const percentageGrade = (grade / total) * 100;

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/public/grade/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          grade: percentageGrade.toFixed(2),
          type: type,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        alert('Error submitting grade: ' + (data.message || 'Please try again.'));
      }
    } catch (err) {
      alert('An error occurred while submitting your grade. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !gradeData) {
    return <InvalidTokenPage />;
  }

  return (
    <div className="min-h-screen bg-gray-100" dir="ltr">
      {/* Header */}
      <header className="bg-blue-700 shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-xl md:text-2xl font-bold text-white">Grade Submission</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Card Header */}
            <div className="bg-blue-50 border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                {gradeData.type === 'entry' ? (
                  <>
                    <h2 className="text-lg font-semibold text-gray-800">Entry Information</h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
                      Entry ID #{gradeData.data.entry_id}
                    </span>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold text-gray-800">Task Information</h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
                      #{gradeData.data.public_task_id || 'N/A'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Course</p>
                  <p className="font-medium text-gray-800">{gradeData.data.course}</p>
                </div>
                {gradeData.type === 'entry' && gradeData.data.entry_date && (
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium text-gray-800">{gradeData.data.entry_date}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form */}
            {!submitted ? (
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label htmlFor="gradeInput" className="block text-sm font-medium text-gray-700">
                    Grade Received
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="number"
                      id="gradeInput"
                      required
                      value={gradeValue}
                      onChange={(e) => setGradeValue(e.target.value)}
                      className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 border p-2"
                      placeholder="Your grade"
                      min="0"
                      step="0.01"
                    />
                    <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                      /
                      <input
                        type="number"
                        id="totalPoints"
                        required
                        value={totalPoints}
                        onChange={(e) => setTotalPoints(e.target.value)}
                        className="w-20 ml-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-0 bg-gray-50 p-1"
                        placeholder="Total"
                        min="1"
                        step="0.01"
                      />
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the grade you received and the total possible points
                  </p>
                </div>

                <div className="pt-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Grade'}
                  </button>
                </div>
              </form>
            ) : (
              /* Success Message */
              <div className="px-6 py-5 bg-green-50">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Grade submitted successfully!
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Thank you for providing your grade. It has been recorded in our system.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Note */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>This is a secure link unique to your {type === 'entry' ? 'entry' : 'task'}.</p>
            <p className="mt-1">If you have any questions, please contact your coordinator.</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-center text-sm text-gray-500">&copy; {new Date().getFullYear()} YMY. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
